import { useContext, useState, useDeferredValue } from 'react';
import { faMagnifyingGlass, faClockRotateLeft, faBan } from '@fortawesome/pro-regular-svg-icons';
import { Avatar, CellBorderWidthEnum, FormSelectOption, IconButton, ModularTable, NotificationDispatch, Pagination, RowType, SortEnum, SwitchThemeContext, TableColorThemeEnum, TextField } from '@tryhackme/thm-ui-components';
import { faUsersGear } from '@fortawesome/pro-light-svg-icons';
import { ThemeColorMode } from 'src/common/enums';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { ToolTip } from 'src/common/components';
import { useBanManageRoomUserMutation, useGetRoomUsersQuery, useResetManageRoomUserProgressMutation } from '../../manage-room.slice';
import { ManageRoomDetail } from '../../manage-room.types';
import { StyledAvatarContainer, StyledBanIcon, StyledDefaultText, StyledHeaderContainer, StyledHeading, StyledModularTableWrapper, StyledOptionsContainer, StyledPaginationContainer, StyledRecordDetailText, StyledRecordsContainer, StyledResetIcon, StyledSearchContainer, StyledSearchIcon, StyledUsersIcon } from './users-tab.styles';
import { paginationOptions } from './users-tab.constants';
import { UserActionsModal, UsersEmptyState, WarningBox } from './components';
export const UsersTab = ({
  roomDetails
}: {
  roomDetails?: ManageRoomDetail;
}) => {
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const roomId = (roomDetails?._id as string);
  const [paginationLimit, setPaginationLimit] = useState(paginationOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(SortEnum.ascending);
  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const [openResetModal, setOpenResetModal] = useState(false);
  const [openBanModal, setOpenBanModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const limit = Number(paginationLimit?.value);
  const [banUser] = useBanManageRoomUserMutation();
  const [resetProgress] = useResetManageRoomUserProgressMutation();
  const {
    data,
    isLoading
  } = useGetRoomUsersQuery({
    roomId,
    page: currentPage,
    limit,
    sortOrder,
    searchText: deferredSearchText
  });
  const users = data?.data?.docs ?? [];
  const totalPages = data?.data?.totalPages ?? 0;
  const totalDocs = data?.data?.totalDocs ?? 0;
  const pagingCounter = data?.data?.pagingCounter ?? 0;
  const hasNextPage = data?.data?.hasNextPage;
  const showEmptyState = Boolean(!users?.length);
  const rows = (users.map(user => ({
    id: user._id,
    'local.username': <StyledAvatarContainer>
        <div>
          <Avatar hasBorder={false} avatarSrc={user.local.avatar} size="3.2rem" />
        </div>

        <StyledDefaultText>{user?.local?.username}</StyledDefaultText>
      </StyledAvatarContainer>,
    reset: <ToolTip toolTipText="Resetting a user’s progress is permanent and cannot be undone.">
        <IconButton data-testid="reset-button" variant="delete" height="3.6rem" width="3.6rem" onClick={() => handleResetUserProgress(user._id)}>
          <StyledResetIcon icon={faClockRotateLeft} />
        </IconButton>
      </ToolTip>,
    ban: <ToolTip toolTipText="Banning a user is permanent and cannot be undone.">
        <IconButton disabled={Boolean(user?.banned)} data-testid="ban-button" variant="delete" height="3.6rem" width="3.6rem" onClick={() => handleBanUser(user._id)}>
          <StyledBanIcon isBanned={Boolean(user?.banned)} icon={faBan} />
        </IconButton>
      </ToolTip>
  })) as RowType[]);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPage(1);
    setSearchText(e.target.value);
  };
  const handleBanUser = (userId: string) => {
    setSelectedUserId(userId);
    setOpenBanModal(true);
  };
  const handleResetUserProgress = (userId: string) => {
    setSelectedUserId(userId);
    setOpenResetModal(true);
  };
  const handlePaginationLimit = (value?: FormSelectOption) => {
    setCurrentPage(1);
    setPaginationLimit(value);
  };
  const handleBanUserApi = async () => {
    try {
      const payload = {
        roomId,
        userId: selectedUserId
      };
      await banUser(payload).unwrap();
      NotificationDispatch('success', 'User is successfully banned from this room.');
    } catch (error) {
      if (isQueryErrorWithMessage(error) && (error.status === 400 || error.status === 403)) {
        NotificationDispatch('error', error.data.message);
      }
      NotificationDispatch('error', 'Uh-oh. There was an error while banning user. Please try again.');
    } finally {
      setOpenBanModal(false);
    }
  };
  const handleResetUserProgressApi = async () => {
    try {
      const payload = {
        roomId,
        userId: selectedUserId
      };
      await resetProgress(payload);
      NotificationDispatch('success', 'User progress has been successfully reset.');
    } catch (error) {
      if (isQueryErrorWithMessage(error) && (error.status === 400 || error.status === 403)) {
        NotificationDispatch('error', error.data.message);
      }
      NotificationDispatch('error', 'Uh-oh. There was an error while resetting user progress. Please try again.');
    } finally {
      setOpenResetModal(false);
    }
  };
  return <section aria-label="Users Tab" data-sentry-component="UsersTab" data-sentry-source-file="users-tab.tsx">
      <StyledHeaderContainer data-sentry-element="StyledHeaderContainer" data-sentry-source-file="users-tab.tsx">
        <StyledUsersIcon icon={faUsersGear} data-sentry-element="StyledUsersIcon" data-sentry-source-file="users-tab.tsx" />
        <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="users-tab.tsx">User Settings</StyledHeading>
      </StyledHeaderContainer>

      <WarningBox warningText="Resetting a user’s progress or banning them is permanent and cannot be undone." data-sentry-element="WarningBox" data-sentry-source-file="users-tab.tsx" />

      <StyledOptionsContainer data-sentry-element="StyledOptionsContainer" data-sentry-source-file="users-tab.tsx">
        <StyledSearchContainer data-sentry-element="StyledSearchContainer" data-sentry-source-file="users-tab.tsx">
          <TextField variant="input" placeholder="Search for username" leftAdornment={<StyledSearchIcon isDarkMode={isDarkMode} icon={faMagnifyingGlass} />} onChange={handleSearchValue} data-sentry-element="TextField" data-sentry-source-file="users-tab.tsx" />
        </StyledSearchContainer>

        <StyledRecordsContainer data-sentry-element="StyledRecordsContainer" data-sentry-source-file="users-tab.tsx">
          <TextField isDisabled={showEmptyState} isMulti={false} variant="select" selected={paginationLimit} options={paginationOptions} isSearchable={false} handleSelection={value => handlePaginationLimit(value)} leftOutsideAdornment={<StyledDefaultText>show</StyledDefaultText>} rightOutsideAdornment={<StyledDefaultText>records</StyledDefaultText>} data-sentry-element="TextField" data-sentry-source-file="users-tab.tsx" />
        </StyledRecordsContainer>
      </StyledOptionsContainer>

      <StyledModularTableWrapper data-sentry-element="StyledModularTableWrapper" data-sentry-source-file="users-tab.tsx">
        <ModularTable isLoading={isLoading} activeSortColumn={showEmptyState ? null : 'local.username'} activeSortOrder={sortOrder} cellBorderWidth={CellBorderWidthEnum.thick} colorTheme={TableColorThemeEnum.grayWhite} columns={[{
        flex: '2.5',
        key: 'local.username',
        sortFunction: () => {
          setSortOrder(prev => prev === SortEnum.ascending ? SortEnum.descending : SortEnum.ascending);
        },
        title: 'Username'
      }, {
        flex: '1',
        key: 'reset',
        justifyContent: 'center',
        title: 'Reset Progress'
      }, {
        flex: '1',
        key: 'ban',
        justifyContent: 'center',
        title: 'Ban User'
      }]} rows={rows} data-sentry-element="ModularTable" data-sentry-source-file="users-tab.tsx" />
        {!isLoading && showEmptyState && <UsersEmptyState />}
      </StyledModularTableWrapper>

      {!isLoading && !showEmptyState && <StyledPaginationContainer>
          <StyledRecordDetailText>{`Showing ${pagingCounter} to ${hasNextPage ? pagingCounter + (limit - 1) : totalDocs} of ${totalDocs} records`}</StyledRecordDetailText>

          <div>
            <Pagination totalPages={totalPages} selectedPage={currentPage} onChange={handlePageChange} />
          </div>
        </StyledPaginationContainer>}

      <UserActionsModal open={openResetModal} openChange={setOpenResetModal} titleText="Reset user’s progress" message="Are you sure you want to reset this user’s progress in this room?" warningBoxText="Resetting a user’s progress is a permanent action and cannot be undone." actionButtonText="Reset progress"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleModalAction={handleResetUserProgressApi} data-sentry-element="UserActionsModal" data-sentry-source-file="users-tab.tsx" />

      <UserActionsModal open={openBanModal} openChange={setOpenBanModal} titleText="Ban user" message="Are you sure you want to ban this user from this room?" warningBoxText="Banning a is a permanent action and cannot be undone." actionButtonText="Ban user"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleModalAction={handleBanUserApi} data-sentry-element="UserActionsModal" data-sentry-source-file="users-tab.tsx" />
    </section>;
};